import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  SelectChangeEvent,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import TableSortLabel from '@mui/material/TableSortLabel';
import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import { DefaultValues, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { SortDirectionDto } from '@/api/generated';
import { CustomTableViewWrapper } from '@/components/table/components/CustomTableViewWrapper.comp.tsx';
import { CustomTableFormBuilder } from '@/components/table/components/formBuilder/CustomTableFormBuilder.comp.tsx';
import { CustomTableAdditionalButtonsModel } from '@/components/table/components/formBuilder/model/CustomTableAdditionalButtons.model.ts';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTablePagination } from '@/components/table/components/pagination/CustomTablePagination.comp.tsx';
import { useCustomTableContext } from '@/components/table/context/CustomTable.context';
import { CustomTableContextProvider } from '@/components/table/context/CustomTableContext.provider.tsx';
import { CustomTableController } from '@/components/table/CustomTable.controller.tsx';
import {
  CustomTableStateDataModel,
  CustomTableStateModel,
} from '@/components/table/model/CustomTableState.model.ts';
import { CustomTableEventModel } from '@/components/table/model/CutomTableEvent.model.ts';
import { CustomTableListViewBoxStyles } from '@/components/table/styles/CustomTable.style.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export interface CustomTableColumn<SORT_BY_COLUMN_NAME_MODEL extends string> {
  align?: TableCellProps['align'];
  id: string;
  label: string;
  sortBy?: SORT_BY_COLUMN_NAME_MODEL;
  sx?: SxProps;
}

export type CustomTableVariant = 'medium' | 'small';

interface BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL extends string, PARAMS extends object> {
  page: number;
  pageSize: number;
  params: PARAMS;
  sortBy?: SORT_BY_COLUMN_NAME_MODEL;
  sortOrder?: SortDirectionDto;
}

type CustomTableProps<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
> = {
  additionalButtons?: CustomTableAdditionalButtonsModel<SEARCH_FORM_MODEL, BUTTONS_PARAMS>[];
  CustomTableRow: CustomTableRowComponent<ROW_ITEM_MODEL>;
  customTableRowAction?: (row: ROW_ITEM_MODEL) => Promise<void> | void;
  getTableDataRequest: (
    req: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  hidePageable?: boolean;
  hidePaper?: boolean;
  idKey: keyof ROW_ITEM_MODEL;
  init: (
    initialParams: CustomTableStateModel<
      SORT_BY_COLUMN_NAME_MODEL,
      SEARCH_FORM_MODEL,
      ROW_ITEM_MODEL
    >,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  initParams: CustomTableStateModel<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>;
  isLoading: boolean;
  isPaginationVisible?: boolean;
  loaderOnInitOnly?: boolean;
  rowsPerPageOptions?: number[];
  size?: CustomTableVariant;
  tableHead: CustomTableColumn<SORT_BY_COLUMN_NAME_MODEL>[];
  tableKey: string;
  tableSx?: SxProps;
  urlStateEnabled?: boolean;
} & (
  | {
      initAlreadyExecuted: boolean;
      loaderOnInitOnly: true;
    }
  | {
      initAlreadyExecuted?: void;
      loaderOnInitOnly?: void;
    }
) &
  (
    | {
        filters: CustomTableFilterDefinition<SEARCH_FORM_MODEL>[];
        validation: ObjectSchema<SEARCH_FORM_MODEL>;
      }
    | {
        filters: null;
        validation?: void;
      }
  );

type CustomTableRowComponent<ROW_ITEM_MODEL extends object> = React.FC<{
  onCustomAction?: (row: ROW_ITEM_MODEL) => Promise<void> | void;
  row: ROW_ITEM_MODEL;
}>;

type CustomTableWrapperProps<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
> = Omit<
  CustomTableProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL, BUTTONS_PARAMS>,
  'init' | 'tableKey' | 'validation'
>;

const DEFAULT_ROWS_PER_PAGE_OPTIONS_WRAPPER = [10, 25, 50];

const CustomTableWrapper = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
>({
  additionalButtons,
  CustomTableRow,
  customTableRowAction,
  filters,
  getTableDataRequest,
  hidePageable,
  hidePaper,
  idKey,
  initAlreadyExecuted,
  initParams,
  isLoading,
  isPaginationVisible = true,
  loaderOnInitOnly,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS_WRAPPER,
  size,
  tableHead,
  tableSx,
}: CustomTableWrapperProps<
  SORT_BY_COLUMN_NAME_MODEL,
  SEARCH_FORM_MODEL,
  ROW_ITEM_MODEL,
  BUTTONS_PARAMS
>) => {
  const { customTableState, setCustomTableState } = useCustomTableContext<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL
  >();

  const { t } = useTranslationWithPrefix('components.table.content');
  const { setValue } = useFormContext<SEARCH_FORM_MODEL>();

  const handleChangePage = async (event: React.ChangeEvent<unknown>, nextPage: number) => {
    event?.stopPropagation();
    event?.preventDefault();
    const data = await getTableDataRequest({
      ...customTableState,
      page: nextPage - 1,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: nextPage - 1,
    }));
  };

  const handleRefreshData = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<Record<string, unknown>>;

      if (customEvent.detail) {
        Object.keys(customEvent.detail).forEach(key => {
          // @ts-expect-error: this is ok
          setValue(key, customEvent.detail[key]);
        });
      }
      void (async () => {
        const response = await getTableDataRequest({
          ...customTableState,
          params: {
            ...customTableState.params,
            ...(customEvent.detail ?? {}),
          },
        });
        setCustomTableState(cur => ({
          ...cur,
          data: {
            ...response,
          },
        }));
      })();
    },
    [customTableState, getTableDataRequest, setCustomTableState, setValue],
  );

  useEffect(() => {
    window.addEventListener(CustomTableEventModel.REFRESH, handleRefreshData);
    return () => {
      window.removeEventListener(CustomTableEventModel.REFRESH, handleRefreshData);
    };
  }, [handleRefreshData]);

  const resolveOrder = (sortOrder: SortDirectionDto) => {
    if (sortOrder === SortDirectionDto.Asc) {
      return SortDirectionDto.Desc;
    }

    return SortDirectionDto.Asc;
  };

  const handleRequestSort = (sortBy: SORT_BY_COLUMN_NAME_MODEL) => async () => {
    const data = await getTableDataRequest({
      ...customTableState,
      sortBy,
      sortOrder:
        sortBy !== customTableState.sortBy
          ? SortDirectionDto.Asc
          : resolveOrder(customTableState.sortOrder),
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      sortBy,
      sortOrder:
        sortBy !== customTableState.sortBy
          ? SortDirectionDto.Asc
          : resolveOrder(customTableState.sortOrder),
    }));
  };

  const handleChangeRowsPerPage = async (event: SelectChangeEvent<number>) => {
    const { value } = event.target;

    if (typeof value !== 'number') {
      return;
    }

    const data = await getTableDataRequest({
      ...customTableState,
      page: 0,
      pageSize: value,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: 0,
      pageSize: value,
    }));
  };

  const isSortActive = (sortBy: SORT_BY_COLUMN_NAME_MODEL) => customTableState.sortBy === sortBy;

  const defineSortDirection = customTableState.sortOrder?.toLowerCase() as
    'asc' | 'desc' | undefined;

  const CustomTableFormBuilderWrapperInner = (
    <CustomTableFormBuilderWrapper
      additionalButtons={additionalButtons}
      filters={filters}
      formRequest={getTableDataRequest}
      hidePaper={hidePaper ?? false}
      initParams={initParams}
      isLoading={isLoading}
      size={size}
    />
  );

  if (isLoading && (!loaderOnInitOnly || !initAlreadyExecuted)) {
    return (
      <CustomTableWrapperInner>
        {CustomTableFormBuilderWrapperInner}
        <CustomTableViewWrapper hidePaper={hidePaper} isLoading size={size} />
      </CustomTableWrapperInner>
    );
  }

  if (customTableState.data.totalResults === 0) {
    return (
      <CustomTableWrapperInner>
        {CustomTableFormBuilderWrapperInner}
        <CustomTableViewWrapper hidePaper={hidePaper} size={size}>
          <Box sx={CustomTableListViewBoxStyles}>
            <Typography variant="f20Regular">{t('noResults')}</Typography>
          </Box>
        </CustomTableViewWrapper>
      </CustomTableWrapperInner>
    );
  }

  return (
    <CustomTableWrapperInner>
      {CustomTableFormBuilderWrapperInner}
      <Divider sx={{ m: 0 }} />
      <CustomTableViewWrapper
        hidePaper={hidePaper}
        size={size}
        sxPaper={{ borderRadius: 0, padding: 0 }}
      >
        <Table size={size} sx={tableSx}>
          <TableHead>
            <TableRow>
              {tableHead.map(cell =>
                cell.sortBy ? (
                  <TableCell align={cell.align} key={cell.id} sx={cell.sx}>
                    <TableSortLabel
                      active={isSortActive(cell.sortBy)}
                      direction={defineSortDirection}
                      onClick={handleRequestSort(cell.sortBy)}
                      sx={{ flexDirection: cell.align === 'right' ? 'row' : undefined }}
                    >
                      {cell.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell align={cell.align} key={cell.id} sx={cell.sx}>
                    {cell.label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {customTableState.data.items.map(row => (
              <CustomTableRow
                key={row[idKey] as string}
                onCustomAction={customTableRowAction}
                row={row}
              />
            ))}
          </TableBody>
        </Table>
        {!hidePageable && (
          <Box sx={hidePaper ? undefined : { pb: 2, px: 3 }}>
            <CustomTablePagination
              count={customTableState.data.totalResults}
              isVisible={isPaginationVisible}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={customTableState.page}
              pageSize={customTableState.pageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              size={size ?? 'medium'}
            />
          </Box>
        )}
      </CustomTableViewWrapper>
    </CustomTableWrapperInner>
  );
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS_TABLE = [5, 10, 15];

export const CustomTable = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
>(
  props: CustomTableProps<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL,
    BUTTONS_PARAMS
  >,
) => {
  const {
    init,
    initParams,
    isPaginationVisible = true,
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS_TABLE,
    tableKey,
    urlStateEnabled = true,
    validation,
    ...restProps
  } = props;
  const formMethods = useForm<SEARCH_FORM_MODEL>({
    defaultValues: initParams.params as DefaultValues<SEARCH_FORM_MODEL>,
    // @ts-expect-error -> https://github.com/react-hook-form/resolvers/issues/648
    resolver: yupResolver(validation),
  });

  return (
    <CustomTableContextProvider customTableState={initParams}>
      <FormProvider {...formMethods}>
        <CustomTableController<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>
          init={init}
          initParams={initParams}
          tableKey={tableKey ?? 'table'}
          urlStateEnabled={urlStateEnabled}
        />
        <CustomTableWrapper<
          SORT_BY_COLUMN_NAME_MODEL,
          SEARCH_FORM_MODEL,
          ROW_ITEM_MODEL,
          BUTTONS_PARAMS
        >
          {...restProps}
          initParams={initParams}
          isPaginationVisible={isPaginationVisible}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </FormProvider>
    </CustomTableContextProvider>
  );
};

type CustomTableFormBuilderWrapperProps<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
> = {
  additionalButtons?: CustomTableAdditionalButtonsModel<SEARCH_FORM_MODEL, BUTTONS_PARAMS>[];
  filters: CustomTableFilterDefinition<SEARCH_FORM_MODEL>[] | null;
  formRequest: (
    req: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  hidePaper: boolean;
  initParams: CustomTableStateModel<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>;
  isLoading: boolean;
  size?: CustomTableVariant;
};

const CustomTableFormBuilderWrapper = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
>(
  props: CustomTableFormBuilderWrapperProps<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL,
    BUTTONS_PARAMS
  >,
) => {
  const { additionalButtons, filters, hidePaper, size, ...restProps } = props;

  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <CustomTableViewWrapper hidePaper={hidePaper} size={size}>
      <CustomTableFormBuilder<
        SORT_BY_COLUMN_NAME_MODEL,
        SEARCH_FORM_MODEL,
        ROW_ITEM_MODEL,
        BUTTONS_PARAMS
      >
        additionalButtons={additionalButtons ?? []}
        filters={filters}
        size={size}
        {...restProps}
      />
    </CustomTableViewWrapper>
  );
};

const CustomTableWrapperInner: React.FC<PropsWithChildren> = props => {
  return (
    <Stack
      sx={{
        alignItems: 'center',
      }}
    >
      {props.children}
    </Stack>
  );
};
